import { plannedAgreementKey } from "../shared/components/VenueAgreementsEditor";

export const SERIES_SPONSOR_LOGO_KEY = "series";
export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
export const DEFAULT_LOCATION_REVEAL_MINUTES = 2 * MINUTES_PER_DAY;
export const STATEMENT_DESCRIPTOR_MIN_LENGTH = 3;
export const STATEMENT_DESCRIPTOR_MAX_LENGTH = 18;
export const STATEMENT_DESCRIPTOR_DISALLOWED = /[<>\\'"*]/g;

let lastChargeKey = 0;

// A key per custom charge row so removing one doesn't shift the rest; it isn't saved.
export const newCustomCharge = (charge = { name: "", default_amount: "", enabled: false }) => {
  lastChargeKey += 1;
  return { ...charge, key: `custom-charge-${lastChargeKey}` };
};

const sponsorEntity = (saved) => ({
  show_sponsor_on_digital_tickets: Boolean(saved.showSponsorOnDigitalTickets),
  sponsor_name: saved.sponsorName || "",
  sponsor_logo_url: saved.sponsorLogoUrl || null,
  sponsor_logo_file_name: saved.sponsorLogoFileName || null,
  sponsor_logo_file_size: saved.sponsorLogoFileSize || null,
});

const showTypeCopy = (saved) => ({
  ticketConfirmationInfo: saved.ticketConfirmationInfo == null ? null : saved.ticketConfirmationInfo,
  sponsor: saved.showSponsorOnDigitalTickets == null ? null : sponsorEntity(saved),
});

export const initialSettings = (setupConfig) => {
  const saved = (setupConfig && setupConfig.settings) || {};
  const offset = saved.locationRevealOffsetMinutes;
  return {
    isPublic: saved.isPublic !== false,
    promoteOnBandsintown: Boolean(saved.promoteOnBandsintown),
    presenter: saved.presenter || "",
    roomIds: saved.roomIds || [],
    googleMarketingPixels: saved.googleMarketingPixels || [],
    facebookMarketingPixels: saved.facebookMarketingPixels || [],
    tiktokMarketingPixels: saved.tiktokMarketingPixels || [],
    spotifyMarketingPixels: saved.spotifyMarketingPixels || [],
    xMarketingPixels: saved.xMarketingPixels || [],
    locationPrivacy: saved.locationPrivacy || "visible",
    secretLocationLabel: saved.secretLocationLabel || "",
    locationRevealOffsetMinutes: offset == null ? null : offset,
    locationRevealUnit: offset != null && offset % MINUTES_PER_DAY !== 0 ? "hours" : "days",
    collectWaitlistInterestEnabled: saved.collectWaitlistInterestEnabled == null ? null : saved.collectWaitlistInterestEnabled,
    tixelWaitlistRedirectEnabled: saved.tixelWaitlistRedirectEnabled == null ? null : saved.tixelWaitlistRedirectEnabled,
    statementDescriptorSuffix: saved.statementDescriptorSuffix || "",
    plannedAgreements: (saved.plannedAgreements || []).map((agreement) => ({ ...agreement, key: plannedAgreementKey() })),
    plannedCustomCharges: (saved.plannedCustomCharges || []).map((charge) => newCustomCharge(charge)),
    confirmationPageTemplateIds: saved.confirmationPageTemplateIds == null ? null : saved.confirmationPageTemplateIds,
    ticketConfirmationInfo: saved.ticketConfirmationInfo || "",
    sponsor: sponsorEntity(saved),
    byShowType: Object.fromEntries(Object.entries(saved.byShowType || {}).map(([showType, copy]) => [showType, showTypeCopy(copy)])),
  };
};

export const enabledConfirmationPageTemplateIds = (settings, templates) =>
  settings.confirmationPageTemplateIds == null
    ? (templates || []).filter((template) => template.enabledByDefault).map((template) => template.id)
    : settings.confirmationPageTemplateIds;

export const statementDescriptorProblem = (suffix) => {
  if (!suffix) return null;
  if (suffix.length < STATEMENT_DESCRIPTOR_MIN_LENGTH) return `Use at least ${STATEMENT_DESCRIPTOR_MIN_LENGTH} characters.`;
  if (!/[a-zA-Z]/.test(suffix)) return "Include at least one letter.";
  return null;
};

export const seriesSettingsRow = (settings) => ({
  is_public: settings.isPublic,
  promote_on_bandsintown: settings.isPublic && settings.promoteOnBandsintown,
  presenter: settings.presenter || null,
  room_ids: settings.roomIds,
  google_marketing_pixels: settings.googleMarketingPixels,
  facebook_marketing_pixels: settings.facebookMarketingPixels,
  tiktok_marketing_pixels: settings.tiktokMarketingPixels,
  spotify_marketing_pixels: settings.spotifyMarketingPixels,
  x_marketing_pixels: settings.xMarketingPixels,
  location_privacy: settings.locationPrivacy,
  secret_location_label: settings.locationPrivacy === "visible" ? null : settings.secretLocationLabel || null,
  location_reveal_offset_minutes: settings.locationPrivacy === "visible" ? null : settings.locationRevealOffsetMinutes,
  collect_waitlist_interest_enabled: settings.collectWaitlistInterestEnabled,
  tixel_waitlist_redirect_enabled: settings.tixelWaitlistRedirectEnabled,
  statement_descriptor_suffix: settings.statementDescriptorSuffix || null,
  planned_agreements: settings.plannedAgreements.map(({ body, required, fan_tag_name }) => ({ body, required, fan_tag_name })),
  planned_custom_charges: settings.plannedCustomCharges.map(({ name, default_amount, enabled }) => ({ name, default_amount, enabled })),
  confirmation_page_template_ids: settings.confirmationPageTemplateIds,
  ticket_confirmation_info: settings.ticketConfirmationInfo || null,
  show_sponsor_on_digital_tickets: settings.sponsor.show_sponsor_on_digital_tickets,
  sponsor_name: settings.sponsor.sponsor_name || null,
  remove_sponsor_logo: Boolean(settings.sponsor.destroy_sponsor_logo),
});

export const showTypeCopyRow = (copy) => {
  const sponsor = copy && copy.sponsor;
  return {
    ticket_confirmation_info: copy && copy.ticketConfirmationInfo != null ? copy.ticketConfirmationInfo : null,
    show_sponsor_on_digital_tickets: sponsor ? sponsor.show_sponsor_on_digital_tickets : null,
    sponsor_name: sponsor ? sponsor.sponsor_name || null : null,
    remove_sponsor_logo: Boolean(sponsor && sponsor.destroy_sponsor_logo),
  };
};

const pendingLogo = (sponsor, uploaded) =>
  sponsor && sponsor.sponsor_logo instanceof File && !uploaded.has(sponsor.sponsor_logo) ? sponsor.sponsor_logo : null;

export const pendingSponsorLogos = (settings, uploaded) =>
  [
    [SERIES_SPONSOR_LOGO_KEY, pendingLogo(settings.sponsor, uploaded)],
    ...Object.entries(settings.byShowType).map(([showType, copy]) => [showType, pendingLogo(copy.sponsor, uploaded)]),
  ]
    .filter(([, file]) => file)
    .map(([key, file]) => ({ key, file }));

export const nextSponsor = (previous, next) => {
  if (next.destroy_sponsor_logo && !(previous && previous.destroy_sponsor_logo)) return { ...next, sponsor_logo: null };
  if (next.sponsor_logo instanceof File && next.sponsor_logo !== (previous && previous.sponsor_logo)) return { ...next, destroy_sponsor_logo: false };
  return next;
};
